import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Users, BookOpen, Calendar, UsersIcon } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { RootState, AppDispatch } from "../../store";
import { fetchStudentStats } from "../../store/slices/studentSlice";
import { StatsCard, StatsCardProps } from "../dashboard/Dashboard";

const StaffDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    stats: { totalActive, newThisMonth, courses, isLoadingStats },
    pagination: studentPagination,
  } = useSelector((state: RootState) => state.student);
  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    dispatch(fetchStudentStats());
  }, [dispatch]);

  // Calculate quick stats
  const totalStudents = totalActive || studentPagination.total || 0;
  const totalCourses = courses.length;
  const newEnrollments = newThisMonth || 0;

  const quickStats: StatsCardProps[] = [
    {
      title: "Active Students",
      value: totalStudents.toLocaleString(),
      icon: UsersIcon,
    },
    {
      title: "Active Course Groups",
      value: totalCourses.toString(),
      icon: BookOpen,
    },
    {
      title: "Enrollments (This month)",
      value: newEnrollments.toString(),
      icon: Calendar,
    },
  ];

  if (isLoadingStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-xl font-bold text-gray-800">
          Welcome back, {user?.firstName} 👋
        </h1>
        <p className="text-sm">
          Here's your student enrollment overview for today
        </p>
      </motion.div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickStats.map((stat, index) => {
          return (
            <StatsCard title={stat.title} value={stat.value} icon={stat.icon} />
          );
        })}
      </div>
      <div className="flex flex-col gap-3">
        <h1 className="text-md font-medium">Course Groups Overview</h1>
        <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs md:grid-cols-2 lg:grid-cols-4">
          {courses.map((course, index) => (
            <StatsCard
              title={`${course.name} (${course.code})`}
              value={course.studentCount.toString()}
              icon={UsersIcon}
            />
          ))}
        </div>
      </div>
      {/* Course Groups Overview */}
      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BookOpen className="h-5 w-5" />
              <span>Course Groups Overview</span>
            </CardTitle>
            <CardDescription>
              Student distribution across different programs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {courses.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 + index * 0.1 }}
                  className="p-4 rounded-lg border border-gray-200 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">
                      {course.name}
                    </h3>
                    <Badge variant={course.isActive ? "default" : "secondary"}>
                      {course.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Code: {course.code}
                  </p>
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium">
                      {course.studentCount} students
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div> */}

      {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <UserPlus className="h-5 w-5" />
                <span>Quick Actions</span>
              </CardTitle>
              <CardDescription>
                Common tasks for student enrollment management
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button className="h-auto p-4 flex-col items-start">
                  <div className="flex items-center space-x-2 mb-2">
                    <UserPlus className="h-5 w-5" />
                    <span className="font-semibold">Enroll New Student</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Add a new student to the system
                  </span>
                </Button>
                
                <Button variant="outline" className="h-auto p-4 flex-col items-start">
                  <div className="flex items-center space-x-2 mb-2">
                    <Users className="h-5 w-5" />
                    <span className="font-semibold">Manage Students</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    View and edit student records
                  </span>
                </Button>
                
                <Button variant="outline" className="h-auto p-4 flex-col items-start">
                  <div className="flex items-center space-x-2 mb-2">
                    <GraduationCap className="h-5 w-5" />
                    <span className="font-semibold">Verify Documents</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Validate academic certificates
                  </span>
                </Button>
                
                <Button variant="outline" className="h-auto p-4 flex-col items-start">
                  <div className="flex items-center space-x-2 mb-2">
                    <BookOpen className="h-5 w-5" />
                    <span className="font-semibold">Generate Reports</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Create enrollment reports
                  </span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div> */}
    </div>
  );
};

export default StaffDashboard;
